/**
 * Past Performances store – single source of truth.
 * GET runs once per session (ensureFetched); after each PUT/DELETE we refetch and update store.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  ArtistService,
  type PastPerformanceApiItem,
  type PastPerformancePutItem,
} from "@/services/artistService";

export type { PastPerformanceApiItem, PastPerformancePutItem };

interface PastPerformancesState {
  data: PastPerformanceApiItem[] | null;
  isLoading: boolean;
  saveLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: PastPerformanceApiItem[] | null) => void;
  fetchPastPerformances: () => Promise<PastPerformanceApiItem[] | null>;
  ensureFetched: () => Promise<PastPerformanceApiItem[] | null>;
  /** PUT new performances then GET to refresh store. */
  addPerformances: (items: PastPerformancePutItem[]) => Promise<void>;
  /** DELETE by id then GET to refresh store. */
  deleteItem: (id: number) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

let inFlightFetch: Promise<PastPerformanceApiItem[] | null> | null = null;

const initialState: Omit<
  PastPerformancesState,
  | "setData"
  | "fetchPastPerformances"
  | "ensureFetched"
  | "addPerformances"
  | "deleteItem"
  | "setLoading"
  | "setError"
  | "reset"
> = {
  data: null,
  isLoading: false,
  saveLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const usePastPerformancesStore = create<PastPerformancesState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setData: (data) => set({ data, error: null }),

      fetchPastPerformances: async () => {
        if (inFlightFetch) return inFlightFetch;
        inFlightFetch = (async () => {
          set({ isLoading: true, error: null });
          try {
            const data = await ArtistService.getPastPerformances();
            set({
              data: data ?? null,
              error: null,
              isLoading: false,
              hasFetchedInSession: true,
            });
            return data;
          } catch (err) {
            set({
              error: "Failed to load past performances",
              isLoading: false,
            });
            return null;
          } finally {
            inFlightFetch = null;
          }
        })();
        return inFlightFetch;
      },

      ensureFetched: async () => {
        const { hasFetchedInSession, fetchPastPerformances, data } = get();
        if (hasFetchedInSession) return data;
        return fetchPastPerformances();
      },

      addPerformances: async (items: PastPerformancePutItem[]) => {
        if (items.length === 0) return;
        set({ saveLoading: true, error: null });
        try {
          await ArtistService.updatePastPerformances(items);
          await get().fetchPastPerformances();
        } catch (err) {
          set({
            error: "Failed to save. Please try again.",
            saveLoading: false,
          });
          throw err;
        } finally {
          set({ saveLoading: false });
        }
      },

      deleteItem: async (id: number) => {
        set({ error: null });
        try {
          await ArtistService.deletePastPerformance(id);
          await get().fetchPastPerformances();
        } catch (err) {
          set({
            error: "Failed to delete. Please try again.",
          });
          throw err;
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      reset: () => {
        inFlightFetch = null;
        set(initialState);
      },
    }),
    {
      name: "past-performances-storage",
      partialize: () => ({}),
    }
  )
);
